<template>
  <div class="nd-app-shell">
    <v-navigation-drawer
      app
      :permanent="$vuetify.breakpoint.mdAndUp"
      :temporary="$vuetify.breakpoint.smAndDown"
      v-model="drawer"
      width="292"
      mini-variant-width="88"
      :mini-variant="isSidebarMini"
      :class="['nd-sidebar', { 'nd-sidebar--mini': isSidebarMini }]"
      color="transparent"
      floating
    >
      <div class="nd-sidebar__brand">
        <router-link to="/home" class="nd-brand">
          <span class="nd-brand__mark">
            <img src="@/assets/images/newdental-logo.svg" alt="ND" />
          </span>
          <span class="nd-brand__name">NewDental</span>
        </router-link>
        <button
          v-if="$vuetify.breakpoint.mdAndUp"
          class="nd-sidebar__collapse"
          type="button"
          :aria-label="isSidebarMini ? 'Expandir menu lateral' : 'Recolher menu lateral'"
          :title="isSidebarMini ? 'Expandir menu' : 'Recolher menu'"
          @click="toggleSidebar"
        >
          <v-icon size="18" color="#5f6b63">
            {{ isSidebarMini ? "mdi-chevron-right" : "mdi-chevron-left" }}
          </v-icon>
        </button>
      </div>

      <button class="nd-sidebar__action" type="button" title="Nova consulta" aria-label="Nova consulta" @click="$router.push('/consultas/novo')">
        <v-icon size="18" color="white">mdi-plus</v-icon>
        <span>Nova consulta</span>
      </button>

      <div class="nd-sidebar__nav">
        <template v-for="(menuGroup, groupIndex) in sidebarMenu">
          <div
            v-if="(!menuGroup.path || $check(menuGroup.path, 'GET')) && menuGroup.children.length"
            :key="groupIndex"
            class="nd-menu-group"
          >
            <div class="nd-menu-group__label">{{ menuGroup.name }}</div>
            <template v-for="(menuOption, index) in menuGroup.children">
              <MenuItem
                v-if="!menuOption.path || $check(menuOption.path, 'GET')"
                :item="menuOption"
                :key="`${groupIndex}-${index}`"
              />
            </template>
          </div>
        </template>
      </div>

      <v-menu min-width="236" offset-y top>
        <template v-slot:activator="{ on, attrs }">
          <button class="nd-sidebar__footer" v-bind="attrs" v-on="on" type="button">
            <v-avatar size="42">
              <img :src="avatarUrl" :alt="userName" />
            </v-avatar>
            <span class="nd-sidebar__footer-copy">
              <strong>{{ userName }}</strong>
              <small>{{ userEmail }}</small>
            </span>
            <v-icon size="18" color="#5f6b63">mdi-chevron-up</v-icon>
          </button>
        </template>
        <v-list dense class="nd-user-menu">
          <template v-for="(menuOption, index) in profileMenu">
            <MenuItem
              :key="index"
              :item="menuOption"
              v-if="!menuOption || !menuOption.children"
            />
          </template>
        </v-list>
      </v-menu>
    </v-navigation-drawer>

    <v-app-bar app flat height="72" class="nd-topbar">
      <v-app-bar-nav-icon
        v-if="$vuetify.breakpoint.smAndDown"
        color="#111816"
        @click.stop="drawer = !drawer"
      />

      <div class="nd-topbar__title">
        <span>{{ pageTitle }}</span>
        <small>{{ pageDescription }}</small>
      </div>

      <v-spacer />

      <v-btn
        v-if="pageAction"
        class="nd-topbar__action"
        color="primary"
        elevation="0"
        :to="pageAction.to"
      >
        <v-icon left size="18">{{ pageAction.icon || "mdi-plus" }}</v-icon>
        {{ pageAction.label }}
      </v-btn>

      <button class="nd-topbar__icon-button" type="button" aria-label="Notificações">
        <v-icon size="20" color="#1d2622">mdi-bell-outline</v-icon>
      </button>

      <button class="nd-topbar__logout d-none d-sm-inline-flex" type="button" @click="logout">
        Sair
      </button>
    </v-app-bar>

    <main class="nd-main">
      <section class="nd-content-panel">
        <router-view />
      </section>
    </main>
  </div>
</template>

<script>
import { eventHub } from "@/main";
import router from "../router";
import MenuItem from "@/layouts/components/MenuItem";
import profileMenu from "@/common/data/profile-menu.data";
import sidebarMenu from "@/common/data/sidebar-menu.data";

const routeMeta = {
  Home: { title: "Visão Geral", description: "Acompanhe indicadores e operações da clínica" },
  AccountManagement: { title: "Minha Conta", description: "Dados da empresa, plano e cobrança" },

  AdminList: {
    title: "Administradores",
    description: "Usuários com acesso operacional à clínica",
    action: { label: "Incluir", to: "/administradores/novo" },
  },
  AdminCreate: { title: "Novo Administrador", description: "Inclua um usuário operacional" },
  AdminDetails: { title: "Detalhes do Administrador", description: "Atualize acesso e dados do usuário" },

  DentistList: {
    title: "Dentistas",
    description: "Profissionais clínicos com acesso ao sistema",
    action: { label: "Incluir", to: "/dentistas/novo" },
  },
  DentistCreate: { title: "Novo Dentista", description: "Inclua um profissional clínico" },
  DentistDetails: { title: "Detalhes do Dentista", description: "Atualize acesso e dados do profissional" },

  RoleList: {
    title: "Cargos",
    description: "Perfis, permissões e responsabilidades da equipe",
    action: { label: "Novo cargo", to: "/cargos/novo" },
  },
  RoleCreate: { title: "Novo Cargo", description: "Configure perfil e permissões de acesso" },
  RoleDetails: { title: "Detalhes do Cargo", description: "Configure perfil e permissões de acesso" },

  PatientList: {
    title: "Pacientes",
    description: "Cadastros, histórico e vínculos clínicos",
    action: { label: "Incluir", to: "/pacientes/novo" },
  },
  PatientCreate: { title: "Novo Paciente", description: "Inclua um novo paciente na clínica" },
  PatientDetails: { title: "Pacientes", description: "Informações cadastrais do paciente" },
  PatientProfile: { title: "Perfil do Paciente", description: "Histórico, exames e atendimentos" },

  InsurancesList: {
    title: "Convênios",
    description: "Operadoras, vínculos e cobertura dos pacientes",
    action: { label: "Novo convênio", to: "/convenios/novo" },
  },
  InsurancesCreate: { title: "Novo Convênio", description: "Cadastre uma nova operadora" },
  InsurancesDetails: { title: "Editar Convênio", description: "Atualize os dados da operadora" },

  OdontogramsList: {
    title: "Odontogramas",
    description: "Mapa clínico odontológico e histórico por paciente",
    action: { label: "Incluir", to: "/odontogramas/novo" },
  },
  OdontogramsCreate: { title: "Novo Odontograma", description: "Registre um novo mapa clínico" },
  OdontogramsDetails: { title: "Odontograma", description: "Detalhes do mapa clínico" },

  PrescriptionsList: {
    title: "Prescrições",
    description: "Receitas, medicamentos e orientações ao paciente",
    action: { label: "Incluir", to: "/prescricoes/novo" },
  },
  PrescriptionsCreate: { title: "Nova Prescrição", description: "Emita uma nova orientação clínica" },
  PrescriptionsEdit: { title: "Detalhes da Prescrição", description: "Revise medicamentos e orientações" },

  DsdList: {
    title: "DSD",
    description: "Planejamentos digitais do sorriso e imagens clínicas",
    action: { label: "Incluir", to: "/dsd/novo" },
  },
  DsdCreate: { title: "Novo DSD", description: "Crie um novo planejamento digital" },
  DsdEdit: { title: "Visualização do DSD", description: "Detalhes do planejamento digital" },
  DsdAssets: { title: "Imagens DSD", description: "Biblioteca visual para planejamentos" },

  AppointmentsList: {
    title: "Consultas",
    description: "Agenda, atendimentos e retornos",
    action: { label: "Incluir", to: "/consultas/novo" },
  },
  AppointmentsCreate: { title: "Nova Consulta", description: "Agende um novo atendimento" },
  AppointmentsEdit: { title: "Detalhes da Consulta", description: "Atualize dados do atendimento" },
  CalendarView: {
    title: "Calendário",
    description: "Agenda visual e eventos do dia",
    action: { label: "Nova consulta", to: "/consultas/novo" },
  },

  TestsList: {
    title: "Exames",
    description: "Solicitações, laboratórios e documentos clínicos",
    action: { label: "Incluir", to: "/exames/novo" },
  },
  TestsCreate: { title: "Novo Exame", description: "Solicite ou registre um exame" },
  TestsEdit: { title: "Detalhes do Exame", description: "Acompanhe solicitação e documentos" },

  ExamTypesList: {
    title: "Tipos de Exame",
    description: "Classificações usadas no fluxo de exames",
    action: { label: "Incluir", to: "/tipos-de-exame/novo" },
  },
  ExamTypesCreate: { title: "Novo Tipo de Exame", description: "Organize uma nova classificação" },
  ExamTypesEdit: { title: "Detalhes do Tipo de Exame", description: "Atualize a classificação" },

  LaboratoriesList: {
    title: "Laboratórios",
    description: "Rede de parceiros para exames e laudos",
    action: { label: "Incluir", to: "/laboratorios/novo" },
  },
  LaboratoriesCreate: { title: "Novo Laboratório", description: "Cadastre um laboratório parceiro" },
  LaboratoriesEdit: { title: "Detalhes do Laboratório", description: "Atualize os dados do parceiro" },

  AllergiesList: {
    title: "Alergias",
    description: "Condições relevantes para segurança clínica",
    action: { label: "Incluir", to: "/alergias/novo" },
  },
  AllergiesCreate: { title: "Nova Alergia", description: "Registre uma nova condição clínica" },
  AllergiesEdit: { title: "Detalhes da Alergia", description: "Atualize sintomas e observações" },

  ProceduresList: {
    title: "Procedimentos",
    description: "Catálogo clínico utilizado nos atendimentos",
    action: { label: "Incluir", to: "/procedimentos/novo" },
  },
  ProceduresCreate: { title: "Novo Procedimento", description: "Cadastre um procedimento clínico" },
  ProceduresEdit: { title: "Detalhes do Procedimento", description: "Atualize o procedimento" },

  SpecialtiesList: {
    title: "Especialidades",
    description: "Áreas de atendimento disponíveis para agenda",
    action: { label: "Incluir", to: "/especialidades/novo" },
  },
  SpecialtiesCreate: { title: "Nova Especialidade", description: "Cadastre uma área de atendimento" },
  SpecialtiesEdit: { title: "Detalhes da Especialidade", description: "Atualize configurações da área" },

  ProposalsList: {
    title: "Orçamentos",
    description: "Propostas, valores e condições comerciais",
    action: { label: "Incluir", to: "/orcamentos/novo" },
  },
  ProposalsCreate: { title: "Novo Orçamento", description: "Monte uma proposta comercial" },
  ProposalsEdit: { title: "Editar Orçamento", description: "Atualize valores e condições" },

};

export default {
  name: "PrivateLayout",

  components: {
    MenuItem,
  },

  data() {
    return {
      drawer: false,
      sidebarMini: false,
      user: {},
      sidebarMenu,
      profileMenu,
    };
  },

  computed: {
    userName() {
      return this.user && this.user.name ? this.user.name : "Administrador";
    },

    userEmail() {
      return this.user && this.user.email ? this.user.email : "admin@newdental.com.br";
    },

    avatarUrl() {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(this.userName)}&background=A04DC2&color=fff`;
    },

    pageTitle() {
      const current = routeMeta[this.$route.name];
      return current ? current.title : "NewDental";
    },

    pageDescription() {
      const current = routeMeta[this.$route.name];
      return current ? current.description : "Gestão odontológica integrada";
    },

    pageAction() {
      const current = routeMeta[this.$route.name];
      return current && current.action ? current.action : null;
    },

    isSidebarMini() {
      return this.sidebarMini && this.$vuetify.breakpoint.mdAndUp;
    },
  },

  created() {
    this.user = (this.$root.user && this.$root.user.data) || this.$root.user || {};
    this.sidebarMini = localStorage.getItem("newdental.sidebarMini") === "true";

    eventHub.$on("close-menu", () => {
      if (this.$vuetify.breakpoint.smAndDown) this.drawer = false;
    });

    eventHub.$on("logout", () => {
      this.logout();
    });
  },

  watch: {
    "$vuetify.breakpoint.smAndDown": {
      immediate: true,
      handler(isMobile) {
        this.drawer = !isMobile;
        if (isMobile) this.sidebarMini = false;
      },
    },
  },

  destroyed() {
    eventHub.$off("close-menu");
    eventHub.$off("logout");
  },

  methods: {
    toggleSidebar() {
      this.sidebarMini = !this.sidebarMini;
      localStorage.setItem("newdental.sidebarMini", String(this.sidebarMini));
    },

    logout() {
      localStorage.clear();
      router.push("/login");
    },
  },
};
</script>
